/**
 * IntegrationHealthPage — `/integration/health`.
 *
 * Live health monitor — polls GET /v1/integration/health on service-audit,
 * which probes each service's /v1/health/ready endpoint.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface ServiceHealth {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  latencyMs: number | null;
  url: string;
}

const STATUS_CONFIG: Record<ServiceHealth['status'], { dot: string; text: string; label: string }> = {
  healthy: { dot: 'bg-green-500', text: 'text-green-700', label: 'Healthy' },
  degraded: { dot: 'bg-yellow-500', text: 'text-yellow-700', label: 'Degraded' },
  down: { dot: 'bg-red-500', text: 'text-red-700', label: 'Down' },
};

export function IntegrationHealthPage(): JSX.Element {
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request<{ services: ServiceHealth[] }>(
        env.serviceAuditBaseUrl,
        '/v1/integration/health',
      );
      setServices(data.services);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadHealth(); }, [loadHealth]);

  const healthyCount = services.filter((s) => s.status === 'healthy').length;
  const downCount = services.filter((s) => s.status === 'down').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integration health"
        description="Live service readiness — polled from each service's health endpoint."
        actions={
          <button
            type="button"
            onClick={() => void loadHealth()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg disabled:opacity-50"
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-3 underline">dismiss</button>
        </div>
      ) : null}

      {lastRefresh ? (
        <p className="text-xs text-muted">Last checked: {lastRefresh.toLocaleTimeString()}</p>
      ) : null}

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-border p-4 text-center">
          <p className="text-xs text-muted">Services probed</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{services.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-border p-4 text-center">
          <p className="text-xs text-muted">Healthy</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{healthyCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-border p-4 text-center">
          <p className="text-xs text-muted">Down</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{downCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {loading && services.length === 0 ? (
          <div className="p-8 text-center text-muted text-sm">Loading health data…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Service</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Latency (ms)</th>
                <th className="p-3 text-left">URL</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => {
                const cfg = STATUS_CONFIG[s.status];
                return (
                  <tr key={s.service} className="border-t border-border hover:bg-gray-50" data-testid="health-row">
                    <td className="p-3 font-medium text-gray-900">{s.service}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-xs ${cfg.text}`}>
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-xs">
                      {s.latencyMs !== null ? s.latencyMs : '—'}
                    </td>
                    <td className="p-3 font-mono text-xs text-muted">{s.url}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
